import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { BarcodeDetector as BarcodeDetectorPolyfill } from 'barcode-detector';
import { IconScan, IconSwitchHorizontal, IconBulb, IconBulbOff } from '@tabler/icons-react';

interface BarcodeScannerProps {
    show: boolean;
    onHide: () => void;
    onScan: (value: string) => void;
}

// Use native BarcodeDetector if available, otherwise use polyfill
const BarcodeDetectorImpl = ('BarcodeDetector' in window)
    ? (window as any).BarcodeDetector
    : BarcodeDetectorPolyfill;

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
    show,
    onHide,
    onScan
}) => {
    const scannerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const detectorRef = useRef<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
    const [torchOn, setTorchOn] = useState(false);
    const [torchSupported, setTorchSupported] = useState(false);

    // For confirmation (prevent false positives)
    const lastCodeRef = useRef<string | null>(null);
    const codeCountRef = useRef(0);
    const REQUIRED_CONFIRMATIONS = 2;

    const stopScanner = useCallback(() => {
        // Stop animation frame
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        // Stop camera stream
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        videoRef.current = null;
        setIsScanning(false);
        setTorchOn(false);
        setTorchSupported(false);
    }, []);

    const handleCodeDetected = useCallback((code: string) => {
        // Require multiple confirmations to prevent false positives
        if (code === lastCodeRef.current) {
            codeCountRef.current++;
        } else {
            lastCodeRef.current = code;
            codeCountRef.current = 1;
        }

        if (codeCountRef.current >= REQUIRED_CONFIRMATIONS) {
            stopScanner();
            onScan(code);
            onHide();
        }
    }, [stopScanner, onScan, onHide, REQUIRED_CONFIRMATIONS]);

    const startScanner = useCallback(async (facing: 'environment' | 'user') => {
        setError(null);
        setTorchSupported(false);
        setTorchOn(false);
        lastCodeRef.current = null;
        codeCountRef.current = 0;

        // Check for multiple cameras
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(d => d.kind === 'videoinput');
            setHasMultipleCameras(videoDevices.length > 1);
        } catch {
            setHasMultipleCameras(false);
        }

        // Wait for DOM
        await new Promise(resolve => setTimeout(resolve, 100));

        if (!scannerRef.current) return;

        try {
            // Get camera stream
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: facing,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                },
                audio: false
            });

            streamRef.current = stream;

            // Create video element
            const video = document.createElement('video');
            video.srcObject = stream;
            video.setAttribute('playsinline', 'true');
            video.setAttribute('muted', 'true');
            video.style.width = '100%';
            video.style.height = 'auto';
            videoRef.current = video;

            // Clear and append to container
            if (scannerRef.current) {
                scannerRef.current.innerHTML = '';
                scannerRef.current.appendChild(video);
            }

            await video.play();

            // Check torch support
            const track = stream.getVideoTracks()[0];
            try {
                const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
                setTorchSupported(!!capabilities.torch);
            } catch {
                setTorchSupported(false);
            }

            // Create barcode detector (native or polyfill)
            detectorRef.current = new BarcodeDetectorImpl({
                formats: [
                    'ean_13', 'ean_8', 'code_128', 'code_39', 'code_93',
                    'upc_a', 'upc_e', 'codabar', 'itf'
                ]
            });

            setIsScanning(true);

            // Detection loop
            const detectLoop = async () => {
                if (!videoRef.current || videoRef.current.readyState !== 4) {
                    animationFrameRef.current = requestAnimationFrame(detectLoop);
                    return;
                }

                try {
                    const barcodes = await detectorRef.current.detect(videoRef.current);
                    if (barcodes.length > 0) {
                        handleCodeDetected(barcodes[0].rawValue);
                    }
                } catch {
                    // Ignore detection errors
                }

                animationFrameRef.current = requestAnimationFrame(detectLoop);
            };

            detectLoop();

        } catch (err) {
            console.error('Scanner error:', err);
            setError('Nepodarilo sa spustiť kameru. Skontrolujte povolenia.');
        }
    }, [handleCodeDetected]);

    const handleToggleTorch = useCallback(async () => {
        try {
            if (streamRef.current) {
                const track = streamRef.current.getVideoTracks()[0];
                const newTorchState = !torchOn;
                await track.applyConstraints({
                    advanced: [{ torch: newTorchState } as MediaTrackConstraintSet]
                });
                setTorchOn(newTorchState);
            }
        } catch (err) {
            console.error('Error toggling torch:', err);
        }
    }, [torchOn]);

    useEffect(() => {
        if (show) {
            startScanner(facingMode);
        }
        return () => {
            stopScanner();
        };
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSwitchCamera = async () => {
        const newFacing = facingMode === 'environment' ? 'user' : 'environment';
        setFacingMode(newFacing);
        stopScanner();
        await startScanner(newFacing);
    };

    const handleClose = () => {
        stopScanner();
        onHide();
    };

    const isNative = 'BarcodeDetector' in window;

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" fullscreen="lg-down">
            <Modal.Header closeButton>
                <Modal.Title>
                    <IconScan size={24} className="me-2" />
                    Skenovanie čiarového kódu
                    {isNative && <small className="text-success ms-2" style={{ fontSize: '0.6em' }}>TURBO</small>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger">{error}</Alert>
                )}
                <div
                    ref={scannerRef}
                    className="barcode-scanner-container"
                    style={{
                        width: '100%',
                        minHeight: '300px',
                        position: 'relative',
                        overflow: 'hidden',
                        background: '#000'
                    }}
                >
                    <style>{`
                        .barcode-scanner-container video {
                            width: 100% !important;
                            height: auto !important;
                            display: block;
                        }
                        @media (max-width: 991.98px) {
                            .barcode-scanner-container {
                                min-height: calc(100vh - 200px) !important;
                            }
                            .barcode-scanner-container video {
                                height: calc(100vh - 200px) !important;
                                object-fit: cover;
                            }
                        }
                    `}</style>
                </div>
                <p className="text-muted text-center mt-3">
                    Nasmerujte kameru na čiarový kód
                </p>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <div>
                    {isScanning && (
                        <>
                            {hasMultipleCameras && (
                                <Button
                                    variant="outline-primary"
                                    onClick={handleSwitchCamera}
                                    className="me-2"
                                    title="Prepnúť kameru"
                                >
                                    <IconSwitchHorizontal size={20} />
                                    <span className="ms-2 d-none d-sm-inline">
                                        {facingMode === 'environment' ? 'Predná' : 'Zadná'}
                                    </span>
                                </Button>
                            )}
                            {torchSupported && (
                                <Button
                                    variant={torchOn ? 'warning' : 'outline-secondary'}
                                    onClick={handleToggleTorch}
                                    title={torchOn ? 'Vypnúť blesk' : 'Zapnúť blesk'}
                                >
                                    {torchOn ? <IconBulb size={20} /> : <IconBulbOff size={20} />}
                                    <span className="ms-2 d-none d-sm-inline">Blesk</span>
                                </Button>
                            )}
                        </>
                    )}
                </div>
                <Button variant="secondary" onClick={handleClose}>
                    Zrušiť
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
